import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import {
  formatFileSize,
  MAX_FILE_SIZE,
  MAX_FILES_LIMIT,
} from "components/Documents/DocumentConstants";
import { useDocumentDropzone } from "components/Documents/hooks/useDocumentDropzone";
import { useUploadWithProgress } from "hooks/useUploadWithProgress";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import {
  useCreatePresignedUploadUrlMutation,
  useDeleteIntakeDocumentMutation,
} from "services/documents-api";
import { useIntakeToken, useToken } from "utils/context";
import { getSessionId, logToSentry } from "utils/sentry";

import type { FormData, IntakeDocumentType } from "../shared/IntakeTypes";

type DocumentUploadIntakeProps = {
  handleFieldChange: (
    field: string,
    value:
      | IntakeDocumentType[]
      | string
      | number
      | boolean
      | Array<{ value: string; label: string }>,
  ) => Promise<void>;
  intakeIdentifier: string | null;
  onUploadStateChange?: (isUploading: boolean, count: number) => void;
  initializeSubmission: () => Promise<string | null | undefined>;
};

export interface DocumentUploadIntakeRef {
  hasUnuploadedFiles: () => boolean;
  isUploading: () => boolean;
  getUploadingCount: () => number;
}

const DocumentUploadIntake = forwardRef<
  DocumentUploadIntakeRef,
  DocumentUploadIntakeProps
>(
  (
    {
      handleFieldChange,
      intakeIdentifier,
      onUploadStateChange,
      initializeSubmission,
    },
    ref,
  ) => {
    const [activeUploads, setActiveUploads] = useState(0);
    // s3_keys of documents currently being deleted, driving the disabled state
    // of their remove button.
    const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set());
    // Synchronous mirror of the in-flight deletes: state updates are async and
    // wouldn't stop two clicks fired within the same tick from both dispatching
    // a request, so we gate on a ref that we mutate immediately.
    const deletingKeysRef = useRef<Set<string>>(new Set());
    const { watch } = useFormContext<FormData>();

    // notify parent when upload state changes
    useEffect(() => {
      onUploadStateChange?.(activeUploads > 0, activeUploads);
    }, [activeUploads, onUploadStateChange]);
    const { accessToken } = useToken();
    const { intakeToken } = useIntakeToken();
    const { startUpload } = useUploadWithProgress();
    const [createPresignedUploadUrl] = useCreatePresignedUploadUrlMutation();
    const [deleteIntakeDocument] = useDeleteIntakeDocumentMutation();

    // watch the documents field to get persisted files
    const existingDocuments =
      (watch("documents") as IntakeDocumentType[]) || [];

    // expose methods to parent component
    useImperativeHandle(ref, () => ({
      hasUnuploadedFiles: () => {
        // since we auto-upload, there are no unuploaded files
        return false;
      },
      isUploading: () => {
        return activeUploads > 0;
      },
      getUploadingCount: () => {
        return activeUploads;
      },
    }));

    const checkFileCountLimit = (files: File[]): boolean => {
      const totalFilesAfterUpload = existingDocuments.length + files.length;
      if (totalFilesAfterUpload > MAX_FILES_LIMIT) {
        const allowedCount = MAX_FILES_LIMIT - existingDocuments.length;
        KaboodleToaster.error(
          `Cannot upload ${files.length} files. Maximum ${MAX_FILES_LIMIT} files allowed. You can upload ${allowedCount} more files.`,
        );

        const uiSessionId = getSessionId();
        logToSentry("Intake document upload - file limit exceeded", "warn", {
          uiSessionId,
          attemptedFileCount: files.length,
          existingFileCount: existingDocuments.length,
          maxLimit: MAX_FILES_LIMIT,
          context: "intake_form",
        });

        return false;
      }
      return true;
    };

    const processFiles = async (files: File[]) => {
      if (files.length === 0) return;

      const uiSessionId = getSessionId();

      // check for duplicate files
      const existingFilenames = new Set(
        existingDocuments.map((doc) => doc.filename),
      );
      const currentBatchFilenames = new Set<string>();
      const duplicateFiles: string[] = [];
      const uniqueFiles: File[] = [];

      files.forEach((file) => {
        const isDuplicateOfExisting = existingFilenames.has(file.name);
        const isDuplicateInBatch = currentBatchFilenames.has(file.name);

        if (isDuplicateOfExisting || isDuplicateInBatch) {
          duplicateFiles.push(file.name);
        } else {
          uniqueFiles.push(file);
          currentBatchFilenames.add(file.name);
        }
      });

      // show toast for duplicates
      if (duplicateFiles.length > 0) {
        const duplicateMessage =
          duplicateFiles.length === 1
            ? `"${duplicateFiles[0]}" is a duplicate and was not uploaded.`
            : `${duplicateFiles.length} duplicate files were not uploaded: ${duplicateFiles.slice(0, 3).join(", ")}${duplicateFiles.length > 3 ? "..." : ""}`;

        KaboodleToaster.info(duplicateMessage);

        logToSentry("Duplicate files detected during upload", "info", {
          uiSessionId,
          duplicateCount: duplicateFiles.length,
          duplicateFiles: duplicateFiles,
          totalFilesAttempted: files.length,
          uniqueFilesCount: uniqueFiles.length,
          intake_identifier: intakeIdentifier,
          context: "intake_form",
        });
      }

      // if all files were duplicates, exit early
      if (uniqueFiles.length === 0) {
        return;
      }

      // check file count limit before processing (intake-specific validation)
      if (!checkFileCountLimit(uniqueFiles)) return;

      // ensure we have an intake identifier - initialize if needed (lazy initialization on first upload)
      let currentIntakeIdentifier = intakeIdentifier;
      if (!currentIntakeIdentifier) {
        try {
          currentIntakeIdentifier = await initializeSubmission();
          if (!currentIntakeIdentifier) {
            KaboodleToaster.error(
              "Failed to initialize form. Please refresh and try again.",
            );
            logToSentry(
              "Intake identifier initialization returned null",
              "error",
              {
                uiSessionId,
                fileCount: uniqueFiles.length,
                context: "intake_form",
              },
            );
            return;
          }
        } catch (error) {
          // initializeSubmission already shows error toast
          logToSentry(
            "Intake identifier initialization failed during file upload",
            "error",
            {
              uiSessionId,
              fileCount: uniqueFiles.length,
              error: error instanceof Error ? error.message : "Unknown error",
              context: "intake_form",
            },
          );
          return;
        }
      }

      // increment active uploads count
      setActiveUploads((prev) => prev + uniqueFiles.length);

      logToSentry("Starting file uploads", "info", {
        uiSessionId,
        fileCount: uniqueFiles.length,
        intake_identifier: currentIntakeIdentifier,
        fileNames: uniqueFiles.map((f) => f.name),
        fileSizes: uniqueFiles.map((f) => f.size),
        context: "intake_form",
      });

      try {
        const uploadPromises = uniqueFiles.map(async (file) => {
          const fileStartTime = Date.now();
          try {
            // get presigned URL from backend
            logToSentry("Requesting presigned URL", "info", {
              uiSessionId,
              filename: file.name,
              fileSize: file.size,
              contentType: file.type,
              intake_identifier: currentIntakeIdentifier,
              context: "intake_form",
            });

            const presignedData = await createPresignedUploadUrl({
              accessToken: accessToken,
              intakeToken: intakeToken,
              data: {
                filename: file.name,
                intake_identifier: currentIntakeIdentifier,
                content_type: file.type || "application/octet-stream",
              },
            }).unwrap();

            logToSentry("Presigned URL received, starting S3 upload", "info", {
              uiSessionId,
              filename: file.name,
              s3_key: presignedData.s3_key,
              context: "intake_form",
            });

            // upload file to S3 with progress tracking
            return new Promise<IntakeDocumentType>((resolve, reject) => {
              startUpload({
                file,
                presignedPostUrl: presignedData.upload_url,
                presignedPostFields: presignedData.fields,
                onComplete: () => {
                  const uploadDuration = Date.now() - fileStartTime;
                  // decrement active uploads count on completion
                  setActiveUploads((prev) => Math.max(0, prev - 1));

                  logToSentry("File upload completed", "info", {
                    uiSessionId,
                    filename: file.name,
                    fileSize: file.size,
                    s3_key: presignedData.s3_key,
                    uploadDurationMs: uploadDuration,
                    intake_identifier: currentIntakeIdentifier,
                    context: "intake_form",
                  });

                  resolve({
                    filename: file.name,
                    s3_key: presignedData.s3_key,
                    type: "UNKNOWN",
                    is_cms_submission_document: false,
                  });
                },
                onError: (error) => {
                  const uploadDuration = Date.now() - fileStartTime;
                  // decrement active uploads count on error
                  setActiveUploads((prev) => Math.max(0, prev - 1));

                  logToSentry("S3 upload failed", "error", {
                    uiSessionId,
                    filename: file.name,
                    fileSize: file.size,
                    s3_key: presignedData.s3_key,
                    uploadDurationMs: uploadDuration,
                    error: error.message,
                    errorType: error.name,
                    intake_identifier: currentIntakeIdentifier,
                    context: "intake_form",
                  });

                  reject(error);
                },
              });
            });
          } catch (error) {
            const uploadDuration = Date.now() - fileStartTime;
            // decrement active uploads count on presigned URL error
            setActiveUploads((prev) => Math.max(0, prev - 1));

            logToSentry("Presigned URL request failed", "error", {
              uiSessionId,
              filename: file.name,
              fileSize: file.size,
              uploadDurationMs: uploadDuration,
              error: error instanceof Error ? error.message : "Unknown error",
              errorType: error instanceof Error ? error.name : typeof error,
              intake_identifier: currentIntakeIdentifier,
              context: "intake_form",
            });

            throw error;
          }
        });

        // wait for all uploads to complete (allows partial success)
        const results = await Promise.allSettled(uploadPromises);

        const successfulUploads = results
          .filter(
            (result): result is PromiseFulfilledResult<IntakeDocumentType> =>
              result.status === "fulfilled",
          )
          .map((result) => result.value);

        const failedUploads = results.filter(
          (result) => result.status === "rejected",
        );

        // combine with existing documents
        if (successfulUploads.length > 0) {
          const updatedDocuments = [...existingDocuments, ...successfulUploads];
          await handleFieldChange("documents", updatedDocuments);

          // log successful uploads aggregate
          logToSentry("Batch upload completed", "info", {
            uiSessionId,
            successfulCount: successfulUploads.length,
            failedCount: failedUploads.length,
            totalDocumentsAfterUpload: updatedDocuments.length,
            successfulFiles: successfulUploads.map((doc) => ({
              filename: doc.filename,
              s3_key: doc.s3_key,
            })),
            intake_identifier: currentIntakeIdentifier,
            context: "intake_form",
          });
        }

        // log if all uploads failed
        if (failedUploads.length > 0 && successfulUploads.length === 0) {
          logToSentry("All uploads in batch failed", "error", {
            uiSessionId,
            failedCount: failedUploads.length,
            attemptedFiles: files.map((f) => f.name),
            intake_identifier: currentIntakeIdentifier,
            context: "intake_form",
          });
        }

        // show summary message
        if (failedUploads.length > 0 && successfulUploads.length > 0) {
          KaboodleToaster.info(
            `Uploaded ${successfulUploads.length} file${successfulUploads.length !== 1 ? "s" : ""}. ${failedUploads.length} file${failedUploads.length !== 1 ? "s" : ""} failed.`,
          );
        } else if (failedUploads.length > 0) {
          KaboodleToaster.error(
            `Failed to upload ${failedUploads.length} file${failedUploads.length !== 1 ? "s" : ""}.`,
          );
        }
      } catch (error) {
        KaboodleToaster.error("Failed to process files. Please try again.");

        // log unexpected processing error
        logToSentry("Unexpected error in file processing", "error", {
          uiSessionId,
          error: error instanceof Error ? error.message : "Unknown error",
          errorType: error instanceof Error ? error.name : typeof error,
          attemptedFilesCount: uniqueFiles.length,
          intake_identifier: currentIntakeIdentifier,
          context: "intake_form",
        });
      }
    };

    const handleDeleteDocument = async (doc: IntakeDocumentType) => {
      // guard against double-clicks while a delete is already in flight
      if (deletingKeysRef.current.has(doc.s3_key)) return;

      const uiSessionId = getSessionId();
      deletingKeysRef.current.add(doc.s3_key);
      setDeletingKeys((prev) => new Set(prev).add(doc.s3_key));

      try {
        await deleteIntakeDocument({
          accessToken: accessToken,
          intakeToken: intakeToken,
          s3Key: doc.s3_key,
        }).unwrap();

        // remove from the persisted documents field so it isn't resubmitted
        const updatedDocuments = existingDocuments.filter(
          (existing) => existing.s3_key !== doc.s3_key,
        );
        await handleFieldChange("documents", updatedDocuments);

        KaboodleToaster.success(`"${doc.filename}" removed.`);

        logToSentry("Intake document deleted", "info", {
          uiSessionId,
          filename: doc.filename,
          s3_key: doc.s3_key,
          remainingDocumentCount: updatedDocuments.length,
          intake_identifier: intakeIdentifier,
          context: "intake_form",
        });
      } catch (error) {
        KaboodleToaster.error(
          `Failed to remove "${doc.filename}". Please try again.`,
        );

        logToSentry("Intake document deletion failed", "error", {
          uiSessionId,
          filename: doc.filename,
          s3_key: doc.s3_key,
          error: error instanceof Error ? error.message : "Unknown error",
          errorType: error instanceof Error ? error.name : typeof error,
          intake_identifier: intakeIdentifier,
          context: "intake_form",
        });
      } finally {
        deletingKeysRef.current.delete(doc.s3_key);
        setDeletingKeys((prev) => {
          const next = new Set(prev);
          next.delete(doc.s3_key);
          return next;
        });
      }
    };

    // Dropzone state lives in the parent body (not a nested component): a
    // component defined inside render is a new type each render, so React would
    // unmount/remount the dropzone -- dropping drag-active and focus state --
    // on every state change such as a remove-button click.
    const remainingSlots = MAX_FILES_LIMIT - existingDocuments.length;
    const canUploadMore = remainingSlots > 0;

    const handleValidatedFileDrop = async (validatedFiles: File[]) => {
      if (!canUploadMore) {
        KaboodleToaster.error(
          `Maximum ${MAX_FILES_LIMIT} files allowed. Please remove some files before uploading more.`,
        );
        return;
      }
      await processFiles(validatedFiles);
    };

    const { getRootProps, getInputProps, isDragActive, open } =
      useDocumentDropzone({
        onDrop: handleValidatedFileDrop,
        disabled: !canUploadMore,
        noClick: true,
        noKeyboard: true,
      });

    const handleClickToUpload = () => {
      if (canUploadMore && open) {
        open();
      }
    };

    return (
      <Card heading="Add Files To Submission" cardNumber={4}>
        <figure className="kaboodle-upload-file">
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? "drag-active" : ""}`}
            style={{ opacity: !canUploadMore ? 0.7 : 1 }}
          >
            <input {...getInputProps()} />
            <span
              onClick={handleClickToUpload}
              style={{
                cursor: canUploadMore ? "pointer" : "default",
              }}
            >
              {activeUploads > 0 ? (
                <div>
                  <b>
                    Uploading {activeUploads} file
                    {activeUploads !== 1 ? "s" : ""}...
                  </b>
                  <br />
                  {canUploadMore && (
                    <small>
                      <b>Drag & Drop</b> or <b>Click</b> to Add More
                    </small>
                  )}
                </div>
              ) : existingDocuments.length > 0 ? (
                <div className="uploaded-files">
                  <p>
                    <b>
                      ✓ {existingDocuments.length} file
                      {existingDocuments.length !== 1 ? "s" : ""} uploaded
                    </b>
                  </p>
                  <div>
                    {canUploadMore ? (
                      <>
                        <b>Drag & Drop</b> or <b>Click</b> to Add More Files
                      </>
                    ) : (
                      <small>Maximum {MAX_FILES_LIMIT} files reached</small>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <b>Drag & Drop</b> or <b>Click</b> to Select Files
                  <br />
                  <small>
                    (Maximum {MAX_FILES_LIMIT} files,{" "}
                    {formatFileSize(MAX_FILE_SIZE)} per file)
                  </small>
                </div>
              )}
            </span>
          </div>

          {existingDocuments.length > 0 && (
            <div className="uploaded-files-list">
              <div className="uploaded-files-header">
                <span className="uploaded-files-label">Uploaded Files:</span>
              </div>
              <ul className="file-list">
                {existingDocuments.map((doc) => {
                  const isDeleting = deletingKeys.has(doc.s3_key);
                  return (
                    <li key={doc.s3_key} className="file-item">
                      <div className="file-icon">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 12L11 14L15 10M20.6179 5.98434C20.4132 5.99472 20.2072 5.99997 20 5.99997C16.9265 5.99997 14.123 4.84453 11.9999 2.94434C9.87691 4.84446 7.07339 5.99985 4 5.99985C3.79277 5.99985 3.58678 5.9946 3.38213 5.98422C3.1327 6.94783 3 7.95842 3 9.00001C3 14.5915 6.82432 19.2898 12 20.622C17.1757 19.2898 21 14.5915 21 9.00001C21 7.95847 20.8673 6.94791 20.6179 5.98434Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="file-name">{doc.filename}</span>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => handleDeleteDocument(doc)}
                        disabled={isDeleting}
                        aria-label={`Remove ${doc.filename}`}
                        title={`Remove ${doc.filename}`}
                        style={{ opacity: isDeleting ? 0.4 : 1 }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </figure>
      </Card>
    );
  },
);

DocumentUploadIntake.displayName = "DocumentUploadIntake";

export default DocumentUploadIntake;
